/*
| Searchable combobox that enhances a native <select> (ported from the teamhero
| prototype). The <select> stays the source of truth — hidden, but kept in the
| DOM and mirrored on selection (with a bubbling `change`) so existing `.value`
| reads and change listeners keep working. People options render an avatar +
| role sublabel; typed options render a colour dot; the rest are plain rows.
*/

export interface ComboItem { value: string; label: string; sublabel?: string; color?: string; initials?: string; dotColor?: string; }
export interface EnhanceOpts { items?: ComboItem[]; placeholder?: string; }
export interface ComboHandle { refresh: () => void; destroy: () => void; }

const esc = (v: unknown): string => String(v ?? '').replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string
));
const norm = (s: string): string => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

let comboSeq = 0;

const itemsFromSelect = (select: HTMLSelectElement): ComboItem[] =>
    [...select.options].map((o) => ({
        value: o.value,
        label: o.textContent ?? '',
        sublabel: o.dataset.sub,
        color: o.dataset.color,
        initials: o.dataset.initials,
        dotColor: o.dataset.dotColor,
    }));

export function enhanceSelect(select: HTMLSelectElement, opts: EnhanceOpts = {}): ComboHandle {
    let items = opts.items ?? itemsFromSelect(select);
    const placeholder = opts.placeholder ?? '';
    const listId = `${select.id || 'combo'}-${++comboSeq}-list`;

    const root = document.createElement('div');
    root.className = 'th-combo';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'th-combo-btn';
    btn.setAttribute('aria-haspopup', 'listbox');
    btn.setAttribute('aria-expanded', 'false');
    const aria = select.getAttribute('aria-label');
    if (aria) btn.setAttribute('aria-label', aria);
    const pop = document.createElement('div');
    pop.className = 'th-combo-pop';
    pop.hidden = true;
    root.append(btn, pop);

    select.style.display = 'none';
    select.after(root);
    btn.disabled = select.disabled;

    const byValue = (v: string): ComboItem | undefined => items.find((i) => String(i.value) === String(v));

    const optionInner = (item: ComboItem, compact: boolean): string => {
        let html = '';
        if (item.initials) html += `<span class="pl-av pl-av--md bg-${esc(item.color)}-300">${esc(item.initials)}</span>`;
        else if (item.dotColor) html += `<span class="e-dot" style="background:${item.dotColor}"></span>`;
        html += `<span class="th-combo-lbl">${esc(item.label)}${item.sublabel && !compact ? `<small>${esc(item.sublabel)}</small>` : ''}</span>`;
        return html;
    };

    const renderBtn = (): void => {
        const sel = byValue(select.value);
        btn.innerHTML = (sel ? optionInner(sel, true) : `<span class="th-combo-ph">${esc(placeholder)}</span>`)
            + '<span class="th-combo-caret" aria-hidden="true">▾</span>';
    };

    let activeIdx = -1;
    let filtered = items;

    const setActiveDesc = (): void => {
        const search = pop.querySelector<HTMLElement>('.th-combo-search');
        const act = pop.querySelector<HTMLElement>('li.is-active[id]');
        if (!search) return;
        if (act) search.setAttribute('aria-activedescendant', act.id);
        else search.removeAttribute('aria-activedescendant');
    };

    const drawList = (q: string): void => {
        const nq = norm(q);
        filtered = items.filter((i) => !nq || norm(`${i.label} ${i.sublabel ?? ''}`).includes(nq));
        activeIdx = Math.max(0, filtered.findIndex((i) => String(i.value) === String(select.value)));
        const ul = pop.querySelector<HTMLElement>('.th-combo-list')!;
        ul.innerHTML = filtered.length
            ? filtered.map((i, idx) => `<li role="option" id="${listId}-opt-${idx}" data-v="${esc(i.value)}" aria-selected="${String(i.value) === String(select.value)}" class="${idx === activeIdx ? 'is-active' : ''}">${optionInner(i, false)}</li>`).join('')
            : '<li class="th-combo-none">—</li>';
        ul.querySelectorAll<HTMLElement>('li[data-v]').forEach((li) => li.addEventListener('click', () => choose(li.dataset.v ?? '')));
        setActiveDesc();
    };

    const onSearchKey = (e: KeyboardEvent): void => {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            if (!filtered.length) return;
            activeIdx = (activeIdx + (e.key === 'ArrowDown' ? 1 : -1) + filtered.length) % filtered.length;
            const lis = pop.querySelectorAll<HTMLElement>('li[data-v]');
            lis.forEach((li, idx) => li.classList.toggle('is-active', idx === activeIdx));
            lis[activeIdx]?.scrollIntoView({ block: 'nearest' });
            setActiveDesc();
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (filtered[activeIdx]) choose(String(filtered[activeIdx].value));
        } else if (e.key === 'Escape') {
            e.stopPropagation(); // close only the popup, not an enclosing modal
            closePop();
            btn.focus();
        }
    };

    const onOutside = (e: Event): void => { if (e.target instanceof Node && !root.contains(e.target)) closePop(); };

    const openPop = (): void => {
        pop.innerHTML = `<input type="text" class="th-combo-search" placeholder="Hledat…" role="combobox" aria-expanded="true" aria-autocomplete="list" aria-controls="${listId}"><ul class="th-combo-list" role="listbox" id="${listId}"></ul>`;
        pop.hidden = false;
        btn.setAttribute('aria-expanded', 'true');
        const search = pop.querySelector<HTMLInputElement>('.th-combo-search')!;
        drawList('');
        search.addEventListener('input', () => drawList(search.value));
        search.addEventListener('keydown', onSearchKey);
        search.focus();
        setTimeout(() => document.addEventListener('pointerdown', onOutside), 0);
    };

    const closePop = (): void => {
        pop.hidden = true;
        btn.setAttribute('aria-expanded', 'false');
        document.removeEventListener('pointerdown', onOutside);
    };

    const choose = (v: string): void => {
        select.value = v;
        renderBtn();
        closePop();
        btn.focus();
        select.dispatchEvent(new Event('change', { bubbles: true }));
    };

    btn.addEventListener('click', () => { if (!btn.disabled) (pop.hidden ? openPop() : closePop()); });
    btn.addEventListener('keydown', (e) => {
        if ((e.key === 'ArrowDown' || e.key === ' ') && pop.hidden) { e.preventDefault(); openPop(); }
    });

    const refresh = (): void => {
        if (!opts.items) items = itemsFromSelect(select);
        btn.disabled = select.disabled;
        renderBtn();
        if (!pop.hidden) drawList(pop.querySelector<HTMLInputElement>('.th-combo-search')?.value ?? '');
    };
    const destroy = (): void => { closePop(); root.remove(); select.style.display = ''; };

    renderBtn();
    return { refresh, destroy };
}
